import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session } from '@supabase/supabase-js';

import { useAppContext } from '../context/AppContext';
import UploadIcon from '../assets/upload.png';
//import axlLogo from '../assets/axl.png';
import Logo from '../components/Logo';
import { useModal } from '../context/ModalContext';
import ApplicationPreview from '../components/ApplicationPreview';
import { Feedback } from '../types/feedback';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { api, handleApiError, UploadLeadMetadata } from '../utils/api';

const ALLOWED_EXTENSIONS = ['.pdf'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB limit
const AXL_IIR_FORM_URL = 'https://forms.clickup.com/90141161720/f/2kyd9y7r-2554/E31VJFBGJIL6OXUSOH';

interface Props {
    session: Session;
}
  
interface LastDeckPreview {
  application_id: number;
  status: string;
  company_name: string;
  creted_at: string;
}

export interface ApplicationData {
    company_name: string;
    status: string;
    created_at: string;
    pdf_data: string; // base64 encoded PDF
    data_intake_form: FormData;
    data_deck_feedback: Feedback;
  }

interface LeadFormData {
  firstName: string;
  lastName: string;
  contactEmail: string;
  heardAbout: string;
  linkedinUrl: string;
  sharePitchDeck: '' | 'yes' | 'no';
}

const initialLeadFormData: LeadFormData = {
  firstName: '',
  lastName: '',
  contactEmail: '',
  heardAbout: '',
  linkedinUrl: '',
  sharePitchDeck: '',
};

const Landing: React.FC<Props> = ({ session }) => {
  const navigate = useNavigate();
  const { setDeckFeedback, setAnalysisData, setUploadedFile, setApplicationId } = useAppContext();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [lastDeck, setLastDeck] = useState<LastDeckPreview | null>(null);
  const [applicationData, setApplicationData] = useState<ApplicationData | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showLeadFormModal, setShowLeadFormModal] = useState(false);
  const [leadFormData, setLeadFormData] = useState<LeadFormData>(initialLeadFormData);
  const [leadFormSaved, setLeadFormSaved] = useState(false);
  const [leadFormStep, setLeadFormStep] = useState<'details' | 'deckShare'>('details');
  const [leadFormError, setLeadFormError] = useState('');
  const [shareDeckStatus, setShareDeckStatus] = useState<'idle' | 'sharing' | 'shared' | 'error'>('idle');
  const [shareDeckMessage, setShareDeckMessage] = useState('');
  const [dataConsentChecked, setDataConsentChecked] = useState(false);

  const { openPitchDeckTips } = useModal();
  const { executeRecaptcha } = useRecaptcha();

  // Custom setError function that ensures we never set objects
  const safeSetError = (errorValue: any) => {
    console.log('safeSetError called with:', errorValue, typeof errorValue);
    
    if (typeof errorValue === 'string') {
      setError(errorValue);
    } else if (errorValue && typeof errorValue === 'object') {
      // Try to extract a meaningful message from the object
      let message = 'An error occurred. Please try again.';
      
      if (errorValue.message && typeof errorValue.message === 'string') {
        message = errorValue.message;
      } else if (errorValue.detail && typeof errorValue.detail === 'string') {
        message = errorValue.detail;
      } else if (errorValue.error && typeof errorValue.error === 'string') {
        message = errorValue.error;
      } else {
        // Try to stringify for debugging
        try {
          const serialized = JSON.stringify(errorValue);
          if (serialized && serialized !== '{}') {
            console.error('Setting error from object:', serialized);
            message = `Error: ${serialized}`;
          }
        } catch (e) {
          console.error('Could not serialize error object:', errorValue);
        }
      }
      
      setError(message);
    } else {
      setError(String(errorValue || 'An error occurred. Please try again.'));
    }
  };

  const handleContinueApplication = async () => {
    if (!lastDeck) return;
    
    try {
      if (!applicationData) return;
      setApplicationId(lastDeck.application_id.toString());
      setDeckFeedback(applicationData.data_deck_feedback);
      setAnalysisData(applicationData.data_intake_form as any);
      
      // Convert base64 PDF to File object
      if (applicationData.pdf_data) {
        try {
          const binaryString = window.atob(applicationData.pdf_data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const file = new File([bytes], `${applicationData.company_name}_pitch_deck.pdf`, { type: 'application/pdf' });
          setUploadedFile(file);
        } catch (err) {
          console.error('Error converting PDF data:', err);
        }
      }
      
      navigate('/deck_suggestions');
    } catch (error) {
      console.error('Error continuing application:', error);
    }
  };

  // Fetch last deck when component mounts
  useEffect(() => {
    const fetchLastDeck = async () => {
      try {
        const response = await api.getUserApplications(session.user.id);
        
        // Check if there are any applications and set the most recent one
        if (response && response.length > 0) {
          setLastDeck(response[0]);
          
          // Fetch application data for the most recent application
          const applicationData = await api.getApplication(response[0].application_id);
          setApplicationData(applicationData);
        }
      } catch (error) {
        console.error('Error fetching last deck:', handleApiError(error));
      }
    };

    if (session?.user?.id) {
      fetchLastDeck();
    }
  }, [session]);

  const validateFile = (file: File): boolean => {
    const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    // Check file extension
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      safeSetError(`Invalid file type: ${file.name}. Only PDF files are allowed.`);
      return false;
    }
    
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      safeSetError(`File too large: ${file.name}. Maximum size allowed is ${formatFileSize(MAX_FILE_SIZE)}.`);
      return false;
    }
    
    return true;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    safeSetError('');
    
    if (!selectedFile) {
      return;
    }

    if (validateFile(selectedFile)) {
      setFile(selectedFile);
    } else {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    safeSetError('');
    const droppedFile = event.dataTransfer.files[0];
    
    if (!droppedFile) {
      return;
    }

    if (validateFile(droppedFile)) {
      setFile(droppedFile);
    } else {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeFile = () => {
    setFile(null);
    setDataConsentChecked(false);
    setLeadFormData(initialLeadFormData);
    setLeadFormSaved(false);
    setLeadFormStep('details');
    setLeadFormError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleOpenLeadForm = () => {
    safeSetError('');
    setLeadFormError('');
    setLeadFormStep('details');
    setShowLeadFormModal(true);
  };

  const handleLeadFormChange = (field: keyof LeadFormData, value: string) => {
    setLeadFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const buildLeadMetadata = (): UploadLeadMetadata => ({
    firstName: leadFormData.firstName.trim(),
    lastName: leadFormData.lastName.trim(),
    contactEmail: leadFormData.contactEmail.trim(),
    heardAbout: leadFormData.heardAbout.trim(),
    linkedinUrl: leadFormData.linkedinUrl.trim(),
    shareContactInfo: true,
    sharePitchDeck: leadFormData.sharePitchDeck === 'yes',
    agreementAccepted: dataConsentChecked,
  });

  const handleLeadFormSubmit = () => {
    if (leadFormStep === 'details') {
      if (!leadFormData.firstName.trim() || !leadFormData.lastName.trim() || !leadFormData.contactEmail.trim() || !leadFormData.heardAbout.trim() || !leadFormData.linkedinUrl.trim()) {
        setLeadFormError('Please add your first name, last name, email, how you heard about us, and LinkedIn URL.');
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadFormData.contactEmail.trim())) {
        setLeadFormError('Please enter a valid email address.');
        return;
      }

      if (!/^https?:\/\/.+/i.test(leadFormData.linkedinUrl.trim())) {
        setLeadFormError('Please enter a valid LinkedIn URL starting with http:// or https://.');
        return;
      }

      setLeadFormError('');
      setLeadFormStep('deckShare');
      return;
    }

    if (!leadFormData.sharePitchDeck) {
      setLeadFormError('Please choose whether you want to share your pitch deck with AXL Ventures.');
      return;
    }

    setLeadFormSaved(true);
    setShowLeadFormModal(false);

    if (!file || !dataConsentChecked) {
      return;
    }

    handleSubmit(buildLeadMetadata());
  };

  const handleAnalyzeClick = () => {
    if (!leadFormSaved) {
      handleOpenLeadForm();
      return;
    }

    handleSubmit(buildLeadMetadata());
  };

  const handleSubmit = async (leadMetadata?: UploadLeadMetadata) => {
    // Clear application-specific localStorage data (for all previous applicationIds)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('analysisData_') || key.startsWith('deckFeedback_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear legacy keys (non-applicationId specific)
    localStorage.removeItem('analysisData');
    localStorage.removeItem('deckFeedback');
    localStorage.removeItem('applicationData');
    localStorage.removeItem('formData');
    
    if (!file) return;
    safeSetError('');

    let progressIntervalId: NodeJS.Timeout | null = null;

    try {
      // Execute reCAPTCHA before file upload
      const recaptchaToken = await executeRecaptcha('UPLOAD_DECK');
      
      if (!recaptchaToken) {
        safeSetError('reCAPTCHA verification failed. Please try again.');
        return;
      }

      const stages = [
        "Starting upload...",
        "Uploading file...",
        "Processing document...",
        "Analyzing content...",
        "Finalizing analysis..."
      ];

      setUploading(true);
      setProgress(0);
      setStatus(stages[0]);

      // Calculate estimated time based on file size
      const fileSizeMB = file.size / (1024 * 1024);
      const estimatedTime = Math.min(250 + fileSizeMB * 2, 300) * 1000;
      const progressInterval = estimatedTime / 100;

      let progress = 0;

      // Start progress simulation
      progressIntervalId = setInterval(() => {
      progress += 1;
      setProgress(progress);

      // Update status based on progress
      if (progress < 15) {
        setStatus(stages[0]);
      } else if (progress < 30) {
        setStatus(stages[1]);
      } else if (progress < 45) {
        setStatus(stages[2]);
      } else if (progress < 80) {
        setStatus(stages[3]);
      } else {
        setStatus(stages[4]);
      }

      if (progress >= 100) {
        if (progressIntervalId) clearInterval(progressIntervalId);
      }
    }, progressInterval);

      // Save the file to context for use in other components
      setUploadedFile(file);
      
      // Use the new authenticated API call (user_id and user_email are now extracted from JWT)
      const data = await api.analyzeDeck(file, recaptchaToken, leadMetadata);
      const { application_id } = data;
      
      // Store application ID in context
      setApplicationId(application_id);
      
      // Ensure progress reaches 100% before navigating
      const currentProgress = progress;
      if (currentProgress < 100) {
        const fastInterval = setInterval(() => {
          progress += 2;
          setProgress(Math.min(progress, 100));
          if (progress >= 100) {
            clearInterval(fastInterval);
            if (progressIntervalId) clearInterval(progressIntervalId);
            setStatus("Upload complete!");
            setTimeout(() => {
              setUploading(false);
              setStatus('');
              setProgress(0);
              navigate('/deck_suggestions');
            }, 500);
          }
        }, 20);
      } else {
        if (progressIntervalId) clearInterval(progressIntervalId);
        setStatus("Upload complete!");
        setTimeout(() => {
          setUploading(false);
          setStatus('');
          setProgress(0);
          navigate('/deck_suggestions');
        }, 500);
      }

    } catch (error: any) {
      if (progressIntervalId) clearInterval(progressIntervalId);
      setUploading(false);
      setProgress(0);
      setStatus('');
      
      // Use safe error setting
      console.error('Upload error:', error);
      safeSetError(handleApiError(error));
    }
  };

  const handleNewApplication = () => {
    setShowConfirmDialog(true);
  };

  const handleShareExistingDeck = async () => {
    if (!lastDeck) return;

    try {
      setShareDeckStatus('sharing');
      setShareDeckMessage('');
      await api.sharePitchDeck(lastDeck.application_id.toString());
      setShareDeckStatus('shared');
      setShareDeckMessage('Pitch deck shared with AXL Ventures for review.');
    } catch (error) {
      console.error('Error sharing pitch deck:', error);
      setShareDeckStatus('error');
      setShareDeckMessage(handleApiError(error));
    }
  };

  const handleConfirmNewApplication = () => {
    // Clear all application data
    setLastDeck(null);
    setApplicationData(null);
    setDeckFeedback(null);
    setAnalysisData(null);
    setUploadedFile(null);
    setApplicationId(null);
    setDataConsentChecked(false);
    setLeadFormData(initialLeadFormData);
    setLeadFormSaved(false);
    setLeadFormStep('details');
    setLeadFormError('');
    
    // Clear all applicationId-specific localStorage data
    // Since we don't know the previous applicationId, we need to clear all possible keys
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('analysisData_') || key.startsWith('deckFeedback_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear legacy keys (non-applicationId specific)
    localStorage.removeItem('analysisData');
    localStorage.removeItem('deckFeedback');
    localStorage.removeItem('applicationData');
    localStorage.removeItem('formData');
    
    setShowConfirmDialog(false);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen relative font-['Avenir_Next']">
        <div className="h-screen max-w-full w-full mx-auto flex flex-col">
          <div className="fixed top-0 left-0 right-0 h-12 bg-black flex items-center px-8 z-50">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <Logo background="light" className="h-4 w-auto" onClick={() => navigate('/')} />
            </div>
          </div>

          <div className="flex-1 p-8 mt-20">
            <div className={`mb-8 text-center ${lastDeck ? '' : 'mt-20'}`}>
              <h1 className="text-[64px] text-[#000] font-['Avenir_Next'] font-semibold m-0 mt-4 leading-none">AXL Pitch Deck Analyzer</h1>
              <p className="text-[#000000] text-[30px] font-['Avenir_Next'] m-0 mt-6 leading-none">Upload Your Pitch Deck to Get Started</p>
              <div className="flex items-baseline justify-center mt-4">
                <span>Powered by</span>
                <span className="ml-2 font-['Borel'] text-[#000]">Slidekick</span>
              </div>
            </div>

            {lastDeck ? (
              <>
                <div className="max-w-4xl mx-auto mb-12">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">Your Latest Deck</h2>
                    <div className="flex gap-4">
                      <a
                        href={AXL_IIR_FORM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-6 py-3 bg-white text-black text-bold rounded-full hover:bg-gray-100 transition-all duration-200 flex items-center gap-2 border border-gray-200"
                      >
                        Apply as IIR
                      </a>
                      <button
                        onClick={handleShareExistingDeck}
                        disabled={shareDeckStatus === 'sharing'}
                        className="px-6 py-3 bg-white text-black text-bold rounded-full hover:bg-gray-100 transition-all duration-200 flex items-center gap-2 border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {shareDeckStatus === 'sharing' ? 'Sharing...' : 'Share Pitch Deck'}
                      </button>
                      <button
                        onClick={handleContinueApplication}
                        className="px-6 py-3 bg-black text-white text-bold rounded-full hover:bg-gray-800 transition-all duration-200 flex items-center gap-2"
                      >
                        View Feedback
                      </button>
                    </div>
                  </div>
                  <ApplicationPreview
                    application_data={applicationData}
                  />
                  {shareDeckMessage && (
                    <div className={`mt-4 rounded-md p-3 text-sm ${shareDeckStatus === 'error' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                      {shareDeckMessage}
                    </div>
                  )}
                </div>
              </>
            ) : (
              // Original upload UI when no deck exists
              <div className="flex flex-col items-center justify-center">
                {(!file && !uploading) && (
                  <div
                    className={`w-[600px] h-[300px] mt-6 border-4 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all duration-300
                      ${isDragging 
                        ? 'border-[#2a2a4a] bg-[#F5F5F5] scale-105' 
                        : 'border-[#D9D9D9] hover:border-[#2a2a4a] hover:bg-[#F5F5F5]'
                      }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                  >
                    <div className="flex flex-col items-center justify-center h-full">
                      <img src={UploadIcon} alt="Upload Icon" className="w-16 h-16 mb-4" />
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileChange}
                        className="hidden"
                        accept={ALLOWED_EXTENSIONS.join(',')}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div onClick={() => fileInputRef.current?.click()}>
                        <p className="text-[32px] text-[#000] font-medium">Upload Your Deck</p>
                        <p className="text-[22px] text-[#000]">to receive feedback on your pitch!</p>
                        <button className="mt-4 px-10 py-2 bg-[#D9D9D9] text-black rounded rounded-full text-[18px] font-medium">Select a File...</button>
                        <p className="text-[12px] text-[#9C9C9C] mt-1">PDF Files Accepted</p>
                      </div>
                    </div>
                  </div>
                )}
                {error && (
                <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-md">
                    {typeof error === 'string' ? error : 'An error occurred. Please try again.'}
                </div>
                )}
                {(file && !uploading) && (
                  <div className="w-[600px] h-[300px] mt-6 flex flex-col gap-4">
                    <div className="w-full h-full border-4 border-dashed border-[#D9D9D9] rounded-lg p-16 flex flex-col justify-center items-center bg-white shadow-sm hover:shadow-md transition-all duration-300">
                      <div className="flex flex-row items-center gap-4 mb-4 w-full">
                        <div className="w-12 h-12 bg-[#F5F5F5] rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-6 h-6 text-[#9C9C9C]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                          </svg>
                        </div>
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="text-[22px] text-[#000] font-medium truncate" title={file.name}>{file.name}</span>
                          <span className="text-[16px] text-[#9C9C9C]">{formatFileSize(file.size)}</span>
                        </div>
                      </div>
                      <button
                        onClick={removeFile}
                        className="px-4 py-2 text-[#9C9C9C] hover:text-[#000] hover:bg-[#F5F5F5] rounded-full transition-all duration-200 flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                        Remove
                      </button>
                    </div>
                  </div>
                )}
                {!uploading && (
                  <div className="mt-12 mb-20 max-w-[600px] w-full flex flex-col items-center gap-6">
                    <button
                      type="button"
                      onClick={handleOpenLeadForm}
                      className="w-full bg-white border border-gray-200 rounded-2xl p-5 text-left shadow-sm hover:shadow-md hover:border-black transition-all duration-200"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-lg font-semibold text-black">Share your information with AXL Ventures</p>
                        </div>
                        <span className="shrink-0 rounded-full bg-black px-4 py-2 text-sm font-medium text-white">
                          Open Form
                        </span>
                      </div>
                    </button>

                    {/* Data Retention Consent Checkbox */}
                    <div className="w-full bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <div className="flex items-start gap-3">
                        <input
                          id="data-consent-main"
                          type="checkbox"
                          checked={dataConsentChecked}
                          onChange={(e) => setDataConsentChecked(e.target.checked)}
                          className="mt-1 h-4 w-4 text-[#2a2a4a] border-gray-300 rounded focus:ring-[#2a2a4a] focus:ring-2"
                        />
                        <label htmlFor="data-consent-main" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                          {' '}I hereby grant AXL Labs a non-exclusive, worldwide, perpetual, irrevocable, royalty-free license and right to access, collect, use and retain my uploaded pitch deck and any other information (collectively, "My Data") that I or any of my representatives provide in connection with AXL Labs' consideration of my application(s) to AXL Labs or in AXL Labs' services to me. Additionally, AXL Labs may collect, analyze, use and process My Data for the purposes of enhancing, improving, optimizing, analyzing the performance of, and further developing its services to me and other founders, including for the purposes of the purposes of analysis, feedback generation, application processing, and platform improvement.
                        </label>
                      </div>
                    </div>

                    <button
                      onClick={handleAnalyzeClick}
                      className={`w-full max-w-[300px] px-10 py-4 ${(!file || !dataConsentChecked || !leadFormSaved) ? 'bg-[#D9D9D9]' : 'bg-black'} text-black rounded-full font-medium hover:bg-[#C5C5C5] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-['Avenir_Next']`}
                      disabled={!file || !dataConsentChecked || !leadFormSaved}
                    >
                      <span className="text-[20px] text-[#FFF] font-medium font-['Avenir_Next']">Analyze New Deck</span>
                    </button>
                  </div>
                )}
      
                {uploading && (
                  <div className="mt-4 w-full max-w-2xl mx-auto flex flex-col items-center justify-center">
                    <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                      <div
                        className="bg-[#57547B] h-4 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-[#000000] text-center text-lg">{status}</p>
                    <p className="text-[#000000] text-center mb-8 text-lg">This will take 1~5 minutes, feel free to leave this page.</p>
                  </div>
                )}
                
              </div>
            )}
          </div>
           {/* What makes a good pitch deck button */}
            {(
              <div className="fixed bottom-16 left-8">
                <button 
                  onClick={openPitchDeckTips}
                  className={`group flex items-center gap-2 bg-white rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.15)] transition-all duration-300 overflow-hidden ${lastDeck ? 'w-12 hover:w-auto' : 'pl-2 pr-6'}`}
                >
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-white rounded-full">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      {/* Main bulb */}
                      <path d="M12 3.5C8.96243 3.5 6.5 5.96243 6.5 9C6.5 11.2255 7.71071 13.1591 9.5 14.1973V15.5C9.5 16.3284 10.1716 17 11 17H13C13.8284 17 14.5 16.3284 14.5 15.5V14.1973C16.2893 13.1591 17.5 11.2255 17.5 9C17.5 5.96243 15.0376 3.5 12 3.5Z" fill="#FFD700" stroke="#2a2a4a" strokeWidth="1"/>
                      {/* Base */}
                      <path d="M9.663 17H14.337M10 19H14M11 21H13" stroke="#2a2a4a" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <span className={`text-[#2a2a4a] font-medium whitespace-nowrap ${lastDeck ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-300 pr-6' : 'opacity-100 pr-2'}`}>
                    What makes a good pitch deck?
                  </span>
                </button>
              </div>
            )}
        </div>

        {/* Floating New Application Button */}
        {lastDeck && !uploading && (
          <div className="fixed bottom-16 right-8 z-50">
            <button
              onClick={handleNewApplication}
              className="bg-white text-black px-6 py-3 rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.15)] transition-all duration-200 flex items-center gap-2 border border-gray-100 font-bold"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              New Deck
            </button>
          </div>
        )}

        {/* Lead Sharing Form */}
        {showLeadFormModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="mb-6 text-center">
                <h3 className="text-3xl font-semibold text-black mb-2">
                  {leadFormStep === 'details' ? 'Tell Us About You' : 'Share Your Pitch Deck?'}
                </h3>
                <p className="text-gray-600">
                  {leadFormStep === 'details'
                    ? 'Add your information so AXL Ventures can follow up with you.'
                    : 'AXL Ventures can review your pitch deck and reach out if it may be a real business opportunity.'}
                </p>
              </div>

              <div className="space-y-5">
                {leadFormStep === 'details' ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="lead-first-name" className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                        <input
                          id="lead-first-name"
                          type="text"
                          value={leadFormData.firstName}
                          onChange={(e) => handleLeadFormChange('firstName', e.target.value)}
                          className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
                        />
                      </div>
                      <div>
                        <label htmlFor="lead-last-name" className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                        <input
                          id="lead-last-name"
                          type="text"
                          value={leadFormData.lastName}
                          onChange={(e) => handleLeadFormChange('lastName', e.target.value)}
                          className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="lead-contact-email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        id="lead-contact-email"
                        type="email"
                        value={leadFormData.contactEmail}
                        onChange={(e) => handleLeadFormChange('contactEmail', e.target.value)}
                        placeholder="you@example.com"
                        className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
                      />
                    </div>

                    <div>
                      <label htmlFor="lead-heard-about" className="block text-sm font-medium text-gray-700 mb-2">How did you hear about us?</label>
                      <input
                        id="lead-heard-about"
                        type="text"
                        value={leadFormData.heardAbout}
                        onChange={(e) => handleLeadFormChange('heardAbout', e.target.value)}
                        placeholder="LinkedIn, referral, event, search, etc."
                        className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
                      />
                    </div>

                    <div>
                      <label htmlFor="lead-linkedin" className="block text-sm font-medium text-gray-700 mb-2">LinkedIn URL</label>
                      <input
                        id="lead-linkedin"
                        type="url"
                        value={leadFormData.linkedinUrl}
                        onChange={(e) => handleLeadFormChange('linkedinUrl', e.target.value)}
                        placeholder="https://www.linkedin.com/in/..."
                        className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          handleLeadFormChange('sharePitchDeck', 'yes');
                          setLeadFormError('');
                        }}
                        className={`rounded-xl border px-4 py-3 text-left transition-all ${leadFormData.sharePitchDeck === 'yes' ? 'border-black bg-black text-white' : 'border-gray-300 bg-white text-black hover:border-black'}`}
                      >
                        Yes, share my deck for review
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleLeadFormChange('sharePitchDeck', 'no');
                          setLeadFormError('');
                        }}
                        className={`rounded-xl border px-4 py-3 text-left transition-all ${leadFormData.sharePitchDeck === 'no' ? 'border-black bg-black text-white' : 'border-gray-300 bg-white text-black hover:border-black'}`}
                      >
                        No, just save my information
                      </button>
                    </div>
                  </div>
                )}

                {leadFormError && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
                    {leadFormError}
                  </div>
                )}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowLeadFormModal(false)}
                  className="px-6 py-3 border border-gray-300 rounded-full hover:bg-gray-100 transition-all duration-200"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleLeadFormSubmit}
                  className="px-6 py-3 bg-black text-white rounded-full hover:bg-gray-800 transition-all duration-200"
                >
                  {leadFormStep === 'details' ? 'Save Information' : file ? 'Continue to Analysis' : 'Save Information'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl">
              <h3 className="text-2xl font-bold text-black mb-4">Start New Deck?</h3>
              <p className="text-gray-600 mb-6">
                This will delete your current deck and start a new one. This action cannot be undone.
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  className="px-6 py-2 border border-gray-300 rounded-full hover:bg-gray-100 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmNewApplication}
                  className="px-6 py-2 bg-black text-white rounded-full hover:bg-[#3a3a5a] transition-all duration-200"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="fixed bottom-0 left-0 right-0 py-3 px-6 text-center bg-white/80 backdrop-blur-sm">
          <p className="text-xs text-gray-400">
            This site is protected by reCAPTCHA and the Google{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
              Terms of Service
            </a>{' '}
            apply.
          </p>
        </footer>
    </div>
  );
};

export default Landing; 